import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TablePagination,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Chip,
  InputAdornment,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  Link as LinkIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  LocationOn as LocationOnIcon,
  Star as StarIcon,
  Group as GroupIcon,
  SupervisorAccount as SupervisorIcon,
  Check as CheckIcon,
  Devices as DevicesIcon,
  Restore as RestoreIcon,
  FileDownload as FileDownloadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Login as LoginIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import axiosInstance from '../utlis/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { Role } from '../data/roles';
import { UserProvider, useUser } from '../provider/UserProvider';
import { useTheme as useCustomTheme } from '../utlis/useTheme';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import styles from './UserManagement.module.css';
import XLSX from 'sheetjs-style';
import { format } from 'date-fns';
import emailjs from '@emailjs/browser';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.05)',
  backgroundColor: '#fafafa',
}));

// Interfaces
interface User {
  id: number;
  name: string;
  email: string;
  password: string;
  readonly: boolean;
  phone: string;
  disabled: boolean;
  map: string;
  coordinate_format: string;
  device_readonly: boolean;
  administrator: boolean;
  latitude: number;
  longitude: number;
  zoom: number;
  device_limit: number;
  user_limit: number;
  expiration_time: string;
  limit_commands: boolean;
  fixed_email: boolean;
  poi_layer: string;
  attributes: Record<string, any>;
  city: string;
  state: string;
  pincode: string;
  points: number;
  current_plan: number | null;
  plan_purchased_at: string | null;
  plan_expires_at: string | null;
  signup_status?: 'pending' | 'approved' | 'rejected';
  created_at: string; // Added created_at field
}

interface FormData {
  name: string;
  email: string;
  password: string;
  phone: string;
  administrator: boolean;
  disabled: boolean;
  readonly: boolean;
  map: string;
  coordinate_format: string;
  device_readonly: boolean;
  latitude: string;
  longitude: string;
  zoom: string;
  device_limit: string;
  user_limit: string;
  expiration_time: string;
  limit_commands: boolean;
  fixed_email: boolean;
  poi_layer: string;
  attributes: Record<string, any>;
  city: string;
  state: string;
  pincode: string;
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { permissions, user: currentUser } = useUser();
  const muiTheme = useMuiTheme();
  const { getPrimaryColor, getPrimaryLightColor, getPrimaryDarkColor } = useCustomTheme();

  // Permission checks
  const canViewUsers = permissions?.includes('USER_LIST');
  const canAddUser = permissions?.includes('USER_ADD');
  const canUpdateUser = permissions?.includes('USER_UPDATE');
  const canDeleteUser = permissions?.includes('USER_DELETE');
  const isAdmin = currentUser?.user?.administrator;
  const canExport = isAdmin || permissions?.includes('EXPORT_ACCESS');

  // State management - Move all hooks before conditional return
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enableDialogOpen, setEnableDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Role management state
  const [roles, setRoles] = useState<Role[]>([]);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [userRolesLoading, setUserRolesLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // User type filtering state
  const [activeTab, setActiveTab] = useState<'all' | 'signup' | 'normal' | 'admin'>('all');

  // Date filter state
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  // Export state
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  // OTP state for export verification
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [enteredOtp, setEnteredOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');

  // Password visibility state - track which user's password is visible
  const [visiblePasswords, setVisiblePasswords] = useState<Set<number>>(new Set());
  const [showPasswordInDialog, setShowPasswordInDialog] = useState(false);

  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    phone: '',
    administrator: false,
    disabled: false,
    readonly: false,
    map: '',
    coordinate_format: '',
    device_readonly: false,
    latitude: '',
    longitude: '',
    zoom: '',
    device_limit: '',
    user_limit: '',
    expiration_time: '',
    limit_commands: false,
    fixed_email: false,
    poi_layer: '',
    attributes: {},
    city: '',
    state: '',
    pincode: '',
  });

  // Helper functions
  const getDefaultExpirationDate = (): string => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    return oneYearFromNow.toISOString();
  };

   useEffect(() => {
      const email = localStorage.getItem('savedEmail') || '';
      setUserEmail(email);
    }, []);

 const isAdmin1 = () => {
    return userEmail === 'acumenadmin@gmail.com' || userEmail === 'acumenretail@gmail.com';
  };
    

  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  const checkForChanges = (originalUser: User | null, currentFormData: FormData) => {
    if (!originalUser) return false;

    return (
      originalUser.name !== currentFormData.name ||
      originalUser.email !== currentFormData.email ||
      originalUser.phone !== currentFormData.phone ||
      originalUser.administrator !== currentFormData.administrator ||
      originalUser.disabled !== currentFormData.disabled ||
      originalUser.readonly !== currentFormData.readonly ||
      originalUser.map !== currentFormData.map ||
      originalUser.coordinate_format !== currentFormData.coordinate_format ||
      originalUser.device_readonly !== currentFormData.device_readonly ||
      originalUser.latitude !== parseFloat(currentFormData.latitude) ||
      originalUser.longitude !== parseFloat(currentFormData.longitude) ||
      originalUser.zoom !== parseFloat(currentFormData.zoom) ||
      originalUser.device_limit !== parseInt(currentFormData.device_limit) ||
      originalUser.user_limit !== parseInt(currentFormData.user_limit) ||
      originalUser.expiration_time !== currentFormData.expiration_time ||
      originalUser.limit_commands !== currentFormData.limit_commands ||
      originalUser.fixed_email !== currentFormData.fixed_email ||
      originalUser.poi_layer !== currentFormData.poi_layer ||
      originalUser.city !== currentFormData.city ||
      originalUser.state !== currentFormData.state ||
      originalUser.pincode !== currentFormData.pincode ||
      currentFormData.password !== ''
    );
  };

  // API Functions
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/gpstracker/userhandler/');
      if (response.data && response.data.status && response.data.responseData) {
        setUsers(response.data.responseData);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setErrorMessage('Error fetching users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Role management API functions
  const fetchRoles = async () => {
    try {
      setRoleLoading(true);
      const response = await axiosInstance.get('/gpstracker/rolehandler/?offset=0&limit=5000');
      if (response.data && response.data.status && response.data.responseData) {
        const apiRoles = response.data.responseData;
        const transformedRoles: Role[] = apiRoles.map((apiRole: any) => ({
          id: apiRole.id,
          name: apiRole.name,
          type: 'custom',
          permissions: [],
          users: 0,
          lastModified: new Date().toISOString().split('T')[0],
        }));
        setRoles(transformedRoles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setErrorMessage('Error fetching roles. Please try again.');
    } finally {
      setRoleLoading(false);
    }
  };

  const fetchUserRoles = async (userId: number) => {
    try {
      setUserRolesLoading(true);
      const response = await axiosInstance.get(`/gpstracker/userroleshandler/${userId}`);
      if (response.data && response.data.status && response.data.responseData) {
        const userRoleIds = response.data.responseData.map((userRole: any) => userRole.role);
        setSelectedRoles(userRoleIds);
      } else {
        setSelectedRoles([]);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setSelectedRoles([]);
    } finally {
      setUserRolesLoading(false);
    }
  };

  const saveUserRoles = async () => {
    if (!selectedUser) return;

    try {
      setRoleLoading(true);
      const payload = {
        user_id: selectedUser.id,
        role_ids: selectedRoles,
      };

      const response = await axiosInstance.post('/gpstracker/userroleshandler/', payload);
      if (response.data && response.data.status) {
        setSuccessMessage('User roles updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setRoleDialogOpen(false);
      } else {
        setErrorMessage('Failed to update user roles. Please try again.');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving user roles:', error);
      setErrorMessage('Error saving user roles. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setRoleLoading(false);
    }
  };

  const handleSaveUser = async () => {
    if (selectedUser && !canUpdateUser) {
      setErrorMessage("You don't have permission to update users.");
      return;
    }
    if (!selectedUser && !canAddUser) {
      setErrorMessage("You don't have permission to add users.");
      return;
    }

    // Validate required location fields
    if (!formData.city.trim()) {
      setErrorMessage('City is required.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    if (!formData.state.trim()) {
      setErrorMessage('State is required.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    if (!formData.pincode.trim()) {
      setErrorMessage('Pincode is required.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setSaving(true);

      if (selectedUser) {
        // Update existing user
        const userData: any = {
          ...selectedUser,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          readonly: formData.readonly,
          administrator: formData.administrator,
          map: formData.map,
          coordinate_format: formData.coordinate_format,
          device_readonly: formData.device_readonly,
          latitude: parseFloat(formData.latitude) || 0,
          longitude: parseFloat(formData.longitude) || 0,
          zoom: parseFloat(formData.zoom) || 0,
          disabled: formData.disabled,
          expiration_time: formData.expiration_time,
          device_limit: parseInt(formData.device_limit) || 0,
          user_limit: parseInt(formData.user_limit) || 0,
          limit_commands: formData.limit_commands,
          fixed_email: formData.fixed_email,
          poi_layer: formData.poi_layer,
          attributes: formData.attributes,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
        };

        if (formData.password) {
          userData.password = formData.password;
        }

        await axiosInstance.put(`/gpstracker/userhandler/${selectedUser.id}`, userData);
        setSuccessMessage('User updated successfully!');
      } else {
        // Add new user
        const userData: any = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          readonly: formData.readonly,
          administrator: formData.administrator,
          map: formData.map,
          coordinate_format: formData.coordinate_format,
          device_readonly: formData.device_readonly,
          latitude: parseFloat(formData.latitude) || 0,
          longitude: parseFloat(formData.longitude) || 0,
          zoom: parseFloat(formData.zoom) || 0,
          disabled: formData.disabled,
          expiration_time: formData.expiration_time,
          device_limit: parseInt(formData.device_limit) || 0,
          user_limit: parseInt(formData.user_limit) || 0,
          limit_commands: formData.limit_commands,
          fixed_email: formData.fixed_email,
          poi_layer: formData.poi_layer,
          attributes: formData.attributes,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
        };

        const response = await axiosInstance.post('/gpstracker/userhandler/', userData);

        // Check authentication first
        const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

        // Get current logged-in user from localStorage
        // Try multiple ways to get the user ID as the structure may vary
        let currentUserId: number | null = null;

        if (isAuthenticated) {
          try {
            const userData = JSON.parse(localStorage.getItem('user') || '{}');

            // Log the structure for debugging
            console.log('User data from localStorage:', userData);

            // Try different possible structures:
            // 1. Direct id: userData.id
            // 2. Nested user: userData.user.id
            // 3. Nested user object: userData.user?.id
            currentUserId = userData?.id || userData?.user?.id || null;

            // If still not found, try to fetch current user from API using email
            if (!currentUserId && userData?.email) {
              try {
                // Fetch all users and find the one matching the current user's email
                const usersResponse = await axiosInstance.get('/gpstracker/userhandler/');
                if (usersResponse.data && usersResponse.data.status && usersResponse.data.responseData) {
                  const allUsers = usersResponse.data.responseData;
                  const currentUser = allUsers.find((u: User) => u.email === userData.email || u.email === userData.user?.email);
                  if (currentUser) {
                    currentUserId = currentUser.id;
                    console.log('Found current user ID from API:', currentUserId);
                  }
                }
              } catch (fetchError) {
                console.error('Error fetching current user from API:', fetchError);
              }
            }

            if (!currentUserId) {
              console.warn('Could not find current user ID. User data structure:', userData);
            }
          } catch (parseError) {
            console.error('Error parsing user data from localStorage:', parseError);
          }
        } else {
          console.warn('User is not authenticated');
        }

        // Link the newly added user with the current user
        if (currentUserId && response.data && response.data.responseData) {
          try {
            // Extract the new user ID from the response
            // Response structure: { responseData: { id: number, ... }, status: true, message: "OK" }
            const newUserId = response.data.responseData.id;

            if (newUserId) {
              const linkPayload = {
                user_id: currentUserId,
                device_id: null,
                group_id: null,
                geofence_id: null,
                notification_id: null,
                calendar_id: null,
                attribute_id: null,
                driver_id: null,
                managed_user_id: newUserId,
                command_id: null,
              };

              await axiosInstance.post('/gpstracker/configurationhandler/', linkPayload);
              setSuccessMessage('User added and linked successfully!');
            } else {
              setSuccessMessage('User added successfully!');
            }
          } catch (linkError) {
            console.error('Error linking user:', linkError);
            // Don't fail the entire operation if linking fails
            setSuccessMessage('User added successfully, but linking failed. Please link manually.');
          }
        } else {
          setSuccessMessage('User added successfully!');
        }
      }

      fetchUsers();
      setDialogOpen(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving user:', error);
      setErrorMessage('Error saving user. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    if (!canDeleteUser) {
      setErrorMessage("You don't have permission to delete users.");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.put(`/gpstracker/userhandler/${selectedUser.id}`, {
        ...selectedUser,
        disabled: true,
      });
      setSuccessMessage('User disabled successfully!');
      fetchUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting user:', error);
      setErrorMessage('Error disabling user. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const handleEnableUser = async () => {
    if (!selectedUser) return;
    if (!canUpdateUser) {
      setErrorMessage("You don't have permission to enable users.");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.put(`/gpstracker/userhandler/${selectedUser.id}`, {
        ...selectedUser,
        disabled: false,
      });
      setSuccessMessage('User enabled successfully!');
      fetchUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error enabling user:', error);
      setErrorMessage('Error enabling user. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
      setEnableDialogOpen(false);
      setSelectedUser(null);
    }
  };

  // Event handlers
  const handleAddUser = () => {
    if (!canAddUser) {
      setErrorMessage("You don't have permission to add users.");
      return;
    }
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      administrator: false,
      disabled: false,
      readonly: false,
      map: '',
      coordinate_format: '',
      device_readonly: false,
      latitude: '',
      longitude: '',
      zoom: '',
      device_limit: '',
      user_limit: '',
      expiration_time: getDefaultExpirationDate(),
      limit_commands: false,
      fixed_email: false,
      poi_layer: '',
      attributes: {},
      city: '',
      state: '',
      pincode: '',
    });
    setHasChanges(false);
    setDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    if (!canUpdateUser) {
      setErrorMessage("You don't have permission to update users.");
      return;
    }
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      administrator: user.administrator || false,
      disabled: user.disabled || false,
      readonly: user.readonly || false,
      map: user.map || '',
      coordinate_format: user.coordinate_format || '',
      device_readonly: user.device_readonly || false,
      latitude: String(user.latitude) || '',
      longitude: String(user.longitude) || '',
      zoom: String(user.zoom) || '',
      device_limit: String(user.device_limit) || '',
      user_limit: String(user.user_limit) || '',
      expiration_time: user.expiration_time || '',
      limit_commands: user.limit_commands || false,
      fixed_email: user.fixed_email || false,
      poi_layer: user.poi_layer || '',
      attributes: user.attributes || {},
      city: user.city || '',
      state: user.state || '',
      pincode: user.pincode || '',
    });
    setHasChanges(false);
    setDialogOpen(true);
  };

  // Role management event handlers
  const handleOpenRoleDialog = async (user: User) => {
    if (!canUpdateUser) {
      setErrorMessage("You don't have permission to manage user roles.");
      return;
    }
    setSelectedUser(user);
    setRoleDialogOpen(true);
    await fetchUserRoles(user.id); // Only fetch user roles since roles are already loaded
  };

  const handleRoleToggle = (roleId: number) => {
    setSelectedRoles((prev) => (prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]));
  };

  const handleCloseRoleDialog = () => {
    setRoleDialogOpen(false);
    setSelectedUser(null);
    setSelectedRoles([]);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Toggle password visibility for a specific user
  const togglePasswordVisibility = (userId: number) => {
    setVisiblePasswords((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Login as user (impersonation)
  const handleLoginAsUser = async (user: User) => {
    if (!user.email || !user.password) {
      setErrorMessage('User email or password is missing. Cannot login as this user.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setLoading(true);

      // Just set impersonation flag - don't save any data
      setIsImpersonating(true);
      localStorage.setItem('isImpersonating', 'true');

      // Login as the target user - ensure email and password are sent correctly
      const loginPayload = {
        email: user.email.trim(),
        password: user.password
      };

      console.log('Logging in as user:', { email: loginPayload.email, passwordLength: loginPayload.password?.length });

      const response = await axiosInstance.post(
        '/gpstracker/usersessionhandler/',
        loginPayload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const responseData = response.data;
      let parsedData;

      if (typeof responseData === 'string') {
        try {
          const cleanedData = responseData.replace(/Infinity/g, 'null');
          parsedData = JSON.parse(cleanedData);
        } catch (parseError) {
          console.error('Error parsing string response:', parseError);
          setErrorMessage('Unexpected response format from server.');
          setIsImpersonating(false);
          localStorage.removeItem('isImpersonating');
          return;
        }
      } else if (typeof responseData === 'object' && responseData !== null) {
        parsedData = responseData;
      } else {
        setErrorMessage('Unexpected response format from server.');
        setIsImpersonating(false);
        localStorage.removeItem('isImpersonating');
        return;
      }

      if (parsedData.responseData) {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify(parsedData.responseData));

        // Dispatch custom login event to trigger theme reload
        window.dispatchEvent(new CustomEvent('userLogin'));

        setSuccessMessage(`Successfully logged in as ${user.name}`);
        setTimeout(() => setSuccessMessage(''), 3000);

        // Reload the page to apply the new user session
        window.location.href = '/';
      } else {
        setErrorMessage('Invalid response from server.');
        setIsImpersonating(false);
        localStorage.removeItem('isImpersonating');
      }
    } catch (error: any) {
      console.error('Error logging in as user:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        requestData: { email: user.email, passwordLength: user.password?.length }
      });
      setErrorMessage(error.response?.data?.message || error.message || 'Failed to login as user. Please try again.');
      setIsImpersonating(false);
      localStorage.removeItem('isImpersonating');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Exit impersonation and return to original user
  const handleExitImpersonation = async () => {
    // Get saved credentials from localStorage (saved during normal login)
    const savedEmail = localStorage.getItem('savedEmail');
    const savedPassword = localStorage.getItem('savedPassword');

    if (!savedEmail || !savedPassword) {
      setErrorMessage('Original user credentials not found. Please login again.');
      localStorage.removeItem('isImpersonating');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      return;
    }

    try {
      setLoading(true);

      // Login back as original user using saved credentials
      const loginPayload = {
        email: savedEmail.trim(),
        password: savedPassword
      };

      console.log('Returning to original user:', { email: loginPayload.email, passwordLength: loginPayload.password?.length });

      const response = await axiosInstance.post(
        '/gpstracker/usersessionhandler/',
        loginPayload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const responseData = response.data;
      let parsedData;

      if (typeof responseData === 'string') {
        try {
          const cleanedData = responseData.replace(/Infinity/g, 'null');
          parsedData = JSON.parse(cleanedData);
        } catch (parseError) {
          console.error('Error parsing string response:', parseError);
          setErrorMessage('Unexpected response format from server.');
          return;
        }
      } else if (typeof responseData === 'object' && responseData !== null) {
        parsedData = responseData;
      } else {
        setErrorMessage('Unexpected response format from server.');
        return;
      }

      if (parsedData.responseData) {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify(parsedData.responseData));
        localStorage.removeItem('isImpersonating');

        setIsImpersonating(false);

        // Dispatch custom login event to trigger theme reload
        window.dispatchEvent(new CustomEvent('userLogin'));

        setSuccessMessage('Returned to original account');
        setTimeout(() => setSuccessMessage(''), 3000);

        // Reload the page to apply the original user session
        window.location.href = '/';
      } else {
        setErrorMessage('Invalid response from server.');
      }
    } catch (error: any) {
      console.error('Error exiting impersonation:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        savedEmail: savedEmail || 'unknown'
      });
      setErrorMessage(error.response?.data?.message || error.message || 'Failed to return to original account. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Generate 6-digit OTP
  const generateOTP = (): string => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Send OTP via EmailJS (browser SDK)
  const sendOTPEmail = async (otp: string) => {
    setOtpSending(true);
    setOtpError('');
    
    try {
      // Using EmailJS browser SDK - No backend needed!
      const templateParams = {
        to_email: 'acumentrack27@gmail.com',
        otp_code: otp,
        from_name: 'Acumen Tracking System',
        message: `Your OTP for exporting user data is: ${otp}. This OTP is valid for 5 minutes.`,
      };

      const response = await emailjs.send(
        'service_txzkllb',      // Your service ID
        'template_8v4fo88',     // Your template ID  
        templateParams
      );

      console.log('EmailJS Response:', response);
      setOtpSuccess('OTP sent successfully to Admin email!');
      setTimeout(() => setOtpSuccess(''), 5000);
      return true;
    } catch (error: any) {
      console.error('EmailJS Error:', error);
      setOtpError(error.text || error.message || 'Failed to send OTP. Please try again.');
      return false;
    } finally {
      setOtpSending(false);
    }
  };

  // Handle OTP dialog open and send OTP
  const handleOpenOtpDialog = async () => {
    const otp = generateOTP();
    setGeneratedOtp(otp);
    setEnteredOtp('');
    setOtpDialogOpen(true);
    
    // Send OTP to email
    await sendOTPEmail(otp);
  };

  // Verify OTP and proceed with export
  const handleVerifyOtpAndExport = () => {
    if (enteredOtp === generatedOtp) {
      setOtpDialogOpen(false);
      setOtpError('');
      setEnteredOtp('');
      // Proceed with actual export
      performExport();
    } else {
      setOtpError('Invalid OTP. Please check and try again.');
    }
  };

  // Export to Excel function (renamed from handleExportExcel)
  const performExport = async () => {
    if (!canExport) {
      setErrorMessage("You don't have permission to export data.");
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    if (filteredUsers.length === 0) {
      setErrorMessage('No users to export.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    setDownloading(true);
    setDownloadProgress(0);

    try {
      const excelData = filteredUsers.map((user) => {
        return {
          'Name': user.name || '',
          'Email': user.email || '',
          'Phone': user.phone || '',
          'Role': user.administrator ? 'Administrator' : 'User',
          'Status': user.disabled ? 'Disabled' : 'Active',
          'City': user.city || '',
          'State': user.state || '',
          'Pincode': user.pincode || '',
          'Points': user.points || 0,
          'Device Limit': user.device_limit || 0,
          'User Limit': user.user_limit || 0,
          'Expiration Time': user.expiration_time ? formatDateForDisplay(user.expiration_time) : '',
          'Plan Purchased At': user.plan_purchased_at ? formatDateForDisplay(user.plan_purchased_at) : '',
          'Plan Expires At': user.plan_expires_at ? formatDateForDisplay(user.plan_expires_at) : '',
          'Created At': user.created_at ? formatDateForDisplay(user.created_at) : '', // Added created_at
        };
      });

      setDownloadProgress(50);

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Users List');

      setDownloadProgress(75);

      XLSX.writeFile(workbook, `Users_List_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`);

      setDownloadProgress(100);
      setSuccessMessage('Users exported successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setErrorMessage('Failed to export users. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 1000);
    }
  };

  // Effects
  useEffect(() => {
    // Initialize EmailJS with your public key
    emailjs.init('_DDifojD8EZvzFU20');
    
    fetchUsers();
    fetchRoles(); // Fetch roles when component loads

    // Check if we're in impersonation mode
    const impersonating = localStorage.getItem('isImpersonating') === 'true';
    if (impersonating) {
      setIsImpersonating(true);
    }
  }, []);

  useEffect(() => {
    if (selectedUser) {
      const changes = checkForChanges(selectedUser, formData);
      setHasChanges(changes);
    }
  }, [formData, selectedUser]);

  // Show error if user doesn't have view permission
  if (!canViewUsers) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 4,
            bgcolor: 'white',
            border: '1px solid',
            borderColor: '#e2e8f0',
          }}
        >
          <Typography
            variant='h6'
            color='error'
            align='center'
          >
            You don't have permission to view users.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Filter users based on search, active tab, and date range
  const filteredUsers = users.filter((user) => {
    // First apply tab filtering
    let tabFiltered = true;
    if (activeTab === 'signup') {
      tabFiltered = user.disabled === false && user?.attributes?.is_signup;
    } else if (activeTab === 'normal') {
      tabFiltered = user.disabled === false && !user.administrator;
    } else if (activeTab === 'admin') {
      tabFiltered = user.administrator;
    }
    // activeTab === 'all' shows all users

    // Then apply search filtering
    const searchFiltered =
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase()) ||
      user.phone.toLowerCase().includes(search.toLowerCase());

    // Apply date filtering - filter by created_at
    let dateFiltered = true;
    if (fromDate) {
      const userCreatedAt = user.created_at ? new Date(user.created_at) : null;
      if (!userCreatedAt || userCreatedAt < fromDate) {
        dateFiltered = false;
      }
    }
    if (toDate && dateFiltered) {
      const userCreatedAt = user.created_at ? new Date(user.created_at) : null;
      if (!userCreatedAt || userCreatedAt > toDate) {
        dateFiltered = false;
      }
    }

    return tabFiltered && searchFiltered && dateFiltered;
  });

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Calculate user counts for tabs
  const userCounts = {
    all: users.length,
    signup: users.filter((user) => user.disabled === false && user?.attributes?.is_signup).length,
    normal: users.filter((user) => user.disabled === false && !user.administrator).length,
    admin: users.filter((user) => user.administrator).length,
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1>User Management</h1>
          <p>Manage user accounts, roles, and permissions</p>
        </div>
        <div className={styles.actionButtons}>
          {isImpersonating && (
            <Tooltip
              title='Exit impersonation and return to original account'
              arrow
              placement='bottom'
            >
              <Button
                variant='outlined'
                onClick={handleExitImpersonation}
                disabled={loading}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderColor: '#f59e0b',
                  color: '#f59e0b',
                  '&:hover': {
                    borderColor: '#d97706',
                    bgcolor: 'rgba(245, 158, 11, 0.05)',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Exit Impersonation
              </Button>
            </Tooltip>
          )}
          {isAdmin1() && (
          <Tooltip
            title={canExport ? 'Export users to Excel (OTP verification required)' : 'You don\'t have permission to export data'}
            arrow
            placement='bottom'
          >
            <Button
              variant='outlined'
              onClick={handleOpenOtpDialog}
              disabled={!canExport || downloading || filteredUsers.length === 0}
              startIcon={downloading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              sx={{
                borderColor: getPrimaryColor(),
                color: getPrimaryColor(),
                '&:hover': {
                  borderColor: getPrimaryLightColor(),
                  bgcolor: alpha(getPrimaryColor(), 0.05),
                },
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              {downloading ? 'Exporting...' : 'Export'}
            </Button>
          </Tooltip>)}
          <button
            className={styles.addButton}
            onClick={handleAddUser}
            disabled={!canAddUser}
          >
            <AddIcon />
            Add User
          </button>
          <Tooltip
            title='Bulk add multiple user'
            arrow
            placement='bottom'
          >
            <Button
              variant='contained'
              onClick={() => navigate('/bulk-add-users')}
              sx={{
                backgroundColor: '#000000',
                '&:hover': {
                  backgroundColor: '#000000',
                },
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textTransform: 'none',
              }}
            >
              Bulk
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert
          severity='success'
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert
          severity='error'
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Filters and Search in Single Row */}
      <div className={styles.filtersAndSearch}>
        {/* User Type Filters */}
        <div className={styles.compactFilters}>
          <button
            className={`${styles.compactFilterBtn} ${activeTab === 'all' ? styles.active : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All ({userCounts.all})
          </button>
          <button
            className={`${styles.compactFilterBtn} ${activeTab === 'signup' ? styles.active : ''}`}
            onClick={() => setActiveTab('signup')}
          >
            Active ({userCounts.signup})
          </button>
          <button
            className={`${styles.compactFilterBtn} ${activeTab === 'normal' ? styles.active : ''}`}
            onClick={() => setActiveTab('normal')}
          >
            Normal ({userCounts.normal})
          </button>
          <button
            className={`${styles.compactFilterBtn} ${activeTab === 'admin' ? styles.active : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            Admin ({userCounts.admin})
          </button>
        </div>

        {/* Date Filters */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="From Date"
              value={fromDate}
              onChange={(newValue) => {
                setFromDate(newValue);
                setPage(0);
              }}
              maxDateTime={toDate || undefined}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: '180px' }
                }
              }}
            />
            <DateTimePicker
              label="To Date"
              value={toDate}
              onChange={(newValue) => {
                setToDate(newValue);
                setPage(0);
              }}
              minDateTime={fromDate || undefined}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: '180px' }
                }
              }}
            />
          </LocalizationProvider>
          {(fromDate || toDate) && (
            <IconButton
              size="small"
              onClick={() => {
                setFromDate(null);
                setToDate(null);
                setPage(0);
              }}
              sx={{
                color: '#64748b',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
              }}
              title="Clear date filters"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>

        {/* Search */}
        <div className={styles.compactSearch}>
          <div className={styles.compactSearchInput}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type='text'
              placeholder='Search users...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <span className={styles.resultCount}>{filteredUsers.length} results</span>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <div className={styles.loadingText}>Loading users...</div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className={styles.emptyState}>
          <UserIcon className={styles.emptyStateIcon} />
          <h3 className={styles.emptyStateTitle}>No users found</h3>
          <p className={styles.emptyStateText}>
            {activeTab === 'all'
              ? 'No users match your search criteria.'
              : activeTab === 'signup'
              ? 'No active users found.'
              : activeTab === 'normal'
              ? 'No normal users found.'
              : 'No administrators found.'}
          </p>
        </div>
      ) : (
        <div className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <h3 className={styles.tableTitle}>
              {activeTab === 'all' ? 'All Users' : activeTab === 'signup' ? 'Active Users' : activeTab === 'normal' ? 'Normal Users' : 'Administrators'}
            </h3>
            <div className={styles.tableBadge}>{filteredUsers.length} users</div>
          </div>
          <table className={styles.customTable}>
            <thead>
              <tr>
                <th>User Details</th>
                <th>Contact</th>
                <th>Role</th>
                <th>Status</th>
                <th>Location</th>
                <th>Points</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
                <th>Created At</th> {/* Added Created At column */}
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className={styles.userCard}>
                      <div className={`${styles.userAvatar} ${user.administrator ? styles.admin : styles.normal}`}>
                        {user.administrator ? <AdminIcon /> : <UserIcon />}
                      </div>
                      <div className={styles.userInfo} style={{ flex: 1 }}>
                        <h4>{user.name}</h4>
                        <p>{user.email}</p>
                        {visiblePasswords.has(user.id) && (
                          <div style={{
                            marginTop: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11px',
                            color: '#64748b'
                          }}>
                            <span style={{ fontWeight: 500 }}>Password:</span>
                            <span style={{
                              fontFamily: 'monospace',
                              backgroundColor: '#f1f5f9',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }}>
                              {user.password || 'N/A'}
                            </span>
                          </div>
                        )}
                      </div>
                      <Tooltip title={visiblePasswords.has(user.id) ? 'Hide Password' : 'Show Password'}>
                        <IconButton
                          size="small"
                          onClick={() => togglePasswordVisibility(user.id)}
                          sx={{
                            color: '#64748b',
                            '&:hover': {
                              bgcolor: 'rgba(0,0,0,0.05)',
                              color: getPrimaryColor()
                            }
                          }}
                        >
                          {visiblePasswords.has(user.id) ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <PhoneIcon style={{ color: '#64748b', fontSize: '16px' }} />
                      <span>{user.phone || 'N/A'}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${user.administrator ? styles.admin : styles.user}`}>
                      {user.administrator ? 'Administrator' : 'User'}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${user.disabled ? styles.disabled : styles.active}`}>{user.disabled ? 'Disabled' : 'Active'}</span>
                  </td>
                  <td>
                    {user.city && user.state ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <LocationOnIcon style={{ color: '#64748b', fontSize: '16px' }} />
                        <span>
                          {user.city}, {user.state}
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#64748b' }}>N/A</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.pointsDisplay}>
                      <StarIcon className={styles.pointsIcon} />
                      {user.points || 0}
                    </div>
                  </td>
                  <td>
                    <div className={styles.tableActions}>
                      <button
                        className={`${styles.actionBtn} ${styles.devices}`}
                        onClick={() => navigate(`/user-devices/${user.id}`)}
                        title='View User Devices'
                      >
                        <DevicesIcon />
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.roles}`}
                        onClick={() => handleOpenRoleDialog(user)}
                        disabled={!canUpdateUser}
                        title='Assign Roles'
                      >
                        <LinkIcon />
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.edit}`}
                        onClick={() => handleEditUser(user)}
                        disabled={!canUpdateUser}
                        title='Edit User'
                      >
                        <EditIcon />
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.login}`}
                        onClick={() => handleLoginAsUser(user)}
                        disabled={user.disabled || !user.password}
                        title='Login as this user'
                      >
                        <LoginIcon />
                      </button>
                      {user.disabled ? (
                        <button
                          className={`${styles.actionBtn} ${styles.restore}`}
                          onClick={() => {
                            setSelectedUser(user);
                            setEnableDialogOpen(true);
                          }}
                          disabled={!canUpdateUser}
                          title='Enable User'
                        >
                          <RestoreIcon />
                        </button>
                      ) : (
                        <button
                          className={`${styles.actionBtn} ${styles.delete}`}
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                          disabled={!canDeleteUser}
                          title='Disable User'
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                  </td>
                  <td>{formatDateForDisplay(user.created_at)}</td> {/* Display created_at */}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component='div'
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid #e2e8f0' }}
          />
        </div>
      )}

      {/* Add/Edit User Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: `linear-gradient(135deg, ${alpha(getPrimaryColor(), 0.05)} 0%, ${alpha(getPrimaryColor(), 0)} 100%)`,
              p: 1,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedUser ? <EditIcon color='primary' /> : <AddIcon color='primary' />}
              <Typography
                variant='h6'
                sx={{
                  background: `linear-gradient(135deg, ${getPrimaryColor()} 0%, ${getPrimaryLightColor()} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 600,
                }}
              >
                {selectedUser ? 'Edit User' : 'Add User'}
                {selectedUser && hasChanges && (
                  <Chip
                    label='Unsaved Changes'
                    color='warning'
                    size='small'
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setDialogOpen(false)}
              sx={{
                bgcolor: 'rgba(0,0,0,0.05)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 3 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Box>
              <Typography
                variant='subtitle1'
                fontWeight='600'
                color='primary'
                sx={{ mb: 2 }}
              >
                Basic Information
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    label='Full Name'
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    label='Email'
                    type='email'
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    label={selectedUser ? 'New Password (leave blank to keep current)' : 'Password'}
                    type={showPasswordInDialog ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!selectedUser}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={() => setShowPasswordInDialog(!showPasswordInDialog)}
                            edge='end'
                            size='small'
                            sx={{ color: '#64748b' }}
                          >
                            {showPasswordInDialog ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {selectedUser && selectedUser.password && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='caption' color='text.secondary'>
                        Current Password:
                      </Typography>
                      <Typography
                        variant='caption'
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: '#f1f5f9',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}
                      >
                        {visiblePasswords.has(selectedUser.id) ? selectedUser.password : '••••••••'}
                      </Typography>
                      <Tooltip title={visiblePasswords.has(selectedUser.id) ? 'Hide Current Password' : 'Show Current Password'}>
                        <IconButton
                          size='small'
                          onClick={() => togglePasswordVisibility(selectedUser.id)}
                          sx={{
                            color: '#64748b',
                            '&:hover': { color: getPrimaryColor() }
                          }}
                        >
                          {visiblePasswords.has(selectedUser.id) ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    label='Phone'
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Location Information */}
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant='subtitle1'
                fontWeight='600'
                color='primary'
                sx={{ mb: 2 }}
              >
                Location Information
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '150px' }}>
                  <TextField
                    fullWidth
                    required
                    label='City'
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: '150px' }}>
                  <TextField
                    fullWidth
                    required
                    label='State'
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: '150px' }}>
                  <TextField
                    fullWidth
                    required
                    label='Pincode'
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  />
                </Box>
              </Box>
            </Box>

            {/* System Settings */}
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant='subtitle1'
                fontWeight='600'
                color='primary'
                sx={{ mb: 2 }}
              >
                System Settings
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    label='Device Limit'
                    type='number'
                    value={formData.device_limit}
                    onChange={(e) => setFormData({ ...formData, device_limit: e.target.value })}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    label='User Limit'
                    type='number'
                    value={formData.user_limit}
                    onChange={(e) => setFormData({ ...formData, user_limit: e.target.value })}
                  />
                </Box>
              </Box>
            </Box>

            {/* Expiration Date */}
            <Box>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label='Expiration Date & Time'
                  value={formData.expiration_time ? new Date(formData.expiration_time) : null}
                  onChange={(newValue: Date | null) =>
                    newValue &&
                    setFormData({
                      ...formData,
                      expiration_time: newValue.toISOString(),
                    })
                  }
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: alpha(getPrimaryColor(), 0.02) }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSaveUser}
            disabled={saving || Boolean(selectedUser && !hasChanges)}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              minWidth: 120,
              bgcolor: getPrimaryColor(),
              boxShadow: `0 2px 8px ${alpha(getPrimaryColor(), 0.3)}`,
              '&:hover': {
                bgcolor: getPrimaryLightColor(),
                boxShadow: `0 4px 12px ${alpha(getPrimaryColor(), 0.4)}`,
              },
            }}
          >
            {saving ? 'Saving...' : selectedUser && !hasChanges ? 'No Changes' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete/Disable Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
            <DeleteIcon color='error' />
            <Typography
              variant='h6'
              color='error.main'
            >
              Confirm Disable
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Alert
            severity='warning'
            sx={{ mb: 2 }}
          >
            This action will disable the user account. The user can be enabled later if needed.
          </Alert>
          <Typography>
            Are you sure you want to disable user <strong>"{selectedUser?.name}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'error.light' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            color='error'
            variant='contained'
            startIcon={<DeleteIcon />}
            sx={{
              bgcolor: 'error.main',
              '&:hover': { bgcolor: 'error.dark' },
            }}
          >
            Disable User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enable Confirmation Dialog */}
      <Dialog
        open={enableDialogOpen}
        onClose={() => setEnableDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
            <RestoreIcon color='success' />
            <Typography
              variant='h6'
              color='success.main'
            >
              Confirm Enable
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Alert
            severity='info'
            sx={{ mb: 2 }}
          >
            This action will enable the user account and allow them to access the system again.
          </Alert>
          <Typography>
            Are you sure you want to enable user <strong>"{selectedUser?.name}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'success.light' }}>
          <Button
            onClick={() => setEnableDialogOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEnableUser}
            color='success'
            variant='contained'
            startIcon={<RestoreIcon />}
            sx={{
              bgcolor: 'success.main',
              '&:hover': { bgcolor: 'success.dark' },
            }}
          >
            Enable User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Assignment Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={handleCloseRoleDialog}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background: `linear-gradient(135deg, ${alpha(getPrimaryColor(), 0.05)} 0%, ${alpha(getPrimaryColor(), 0)} 100%)`,
              p: 1,
              borderRadius: 2,
            }}
          >
            <SecurityIcon sx={{ color: getPrimaryColor() }} />
            <Typography
              variant='h6'
              sx={{
                background: `linear-gradient(135deg, ${getPrimaryColor()} 0%, ${getPrimaryLightColor()} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 600,
              }}
            >
              Assign Roles to "{selectedUser?.name}"
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {(roleLoading || userRolesLoading) && (
            <Box
              display='flex'
              justifyContent='center'
              my={3}
            >
              <CircularProgress sx={{ color: getPrimaryColor() }} />
            </Box>
          )}

          {errorMessage && (
            <Alert
              severity='error'
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {errorMessage}
            </Alert>
          )}

          {!roleLoading && !userRolesLoading && roles.length === 0 && (
            <Box
              textAlign='center'
              py={3}
              sx={{
                background: `linear-gradient(135deg, ${alpha(getPrimaryColor(), 0.02)} 0%, ${alpha(getPrimaryColor(), 0)} 100%)`,
                borderRadius: 2,
              }}
            >
              <SecurityIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography
                variant='h6'
                color='text.secondary'
                gutterBottom
              >
                No Roles Available
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
              >
                There are no roles defined in the system yet.
              </Typography>
            </Box>
          )}

          {!roleLoading && !userRolesLoading && roles.length > 0 && (
            <>
              <Alert
                severity='info'
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: getPrimaryColor() },
                }}
              >
                Select the roles you want to assign to this user. Users can have multiple roles.
              </Alert>

              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  sx={{
                    background: `linear-gradient(135deg, ${getPrimaryColor()} 0%, ${getPrimaryLightColor()} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Available Roles ({roles.length})
                </Typography>
                <Chip
                  label={`${selectedRoles.length} selected`}
                  sx={{
                    bgcolor: alpha(getPrimaryColor(), 0.1),
                    color: getPrimaryColor(),
                    fontWeight: 500,
                  }}
                />
              </Box>

              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {roles.map((role) => (
                  <ListItem
                    key={role.id}
                    sx={{
                      border: '1px solid',
                      borderColor: selectedRoles.includes(role.id) ? getPrimaryColor() : 'divider',
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: selectedRoles.includes(role.id) ? alpha(getPrimaryColor(), 0.05) : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: alpha(getPrimaryColor(), 0.02),
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedRoles.includes(role.id)}
                        onChange={() => handleRoleToggle(role.id)}
                        sx={{
                          color: getPrimaryColor(),
                          '&.Mui-checked': {
                            color: getPrimaryColor(),
                          },
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          display='flex'
                          alignItems='center'
                          gap={1}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(getPrimaryColor(), 0.1),
                              color: getPrimaryColor(),
                              width: 24,
                              height: 24,
                            }}
                          >
                            <SecurityIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                          <Typography
                            variant='subtitle2'
                            fontWeight='medium'
                          >
                            {role.name}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ mt: 0.5 }}
                        >
                          Role ID: {role.id} • Type: {role.type}
                        </Typography>
                      }
                    />
                    {selectedRoles.includes(role.id) && <CheckCircleIcon sx={{ color: getPrimaryColor() }} />}
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(getPrimaryColor(), 0.02) }}>
          <Button
            onClick={handleCloseRoleDialog}
            variant='outlined'
            sx={{
              borderColor: getPrimaryColor(),
              color: getPrimaryColor(),
              '&:hover': {
                borderColor: getPrimaryLightColor(),
                bgcolor: alpha(getPrimaryColor(), 0.05),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={saveUserRoles}
            variant='contained'
            disabled={roleLoading || userRolesLoading}
            startIcon={<CheckCircleIcon />}
            sx={{
              bgcolor: getPrimaryColor(),
              boxShadow: `0 2px 8px ${alpha(getPrimaryColor(), 0.3)}`,
              '&:hover': {
                bgcolor: getPrimaryLightColor(),
                boxShadow: `0 4px 12px ${alpha(getPrimaryColor(), 0.4)}`,
              },
            }}
          >
            Save Roles ({selectedRoles.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP Verification Dialog */}
      <Dialog
        open={otpDialogOpen}
        onClose={() => !otpSending && setOtpDialogOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background: `linear-gradient(135deg, ${alpha(getPrimaryColor(), 0.05)} 0%, ${alpha(getPrimaryColor(), 0)} 100%)`,
              p: 1,
              borderRadius: 2,
            }}
          >
            <SecurityIcon sx={{ color: getPrimaryColor() }} />
            <Typography
              variant='h6'
              sx={{
                background: `linear-gradient(135deg, ${getPrimaryColor()} 0%, ${getPrimaryLightColor()} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 600,
              }}
            >
              OTP Verification Required
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity='info' sx={{ borderRadius: 2 }}>
              An OTP has been sent to <strong>admin email</strong>. Please enter the 6-digit code to proceed with export.
            </Alert>

            {otpSending && (
              <Box display='flex' justifyContent='center' alignItems='center' gap={1}>
                <CircularProgress size={20} />
                <Typography variant='body2' color='text.secondary'>
                  Sending OTP...
                </Typography>
              </Box>
            )}

            {otpSuccess && (
              <Alert severity='success' sx={{ borderRadius: 2 }}>
                {otpSuccess}
              </Alert>
            )}

            {otpError && (
              <Alert severity='error' sx={{ borderRadius: 2 }}>
                {otpError}
              </Alert>
            )}

            <TextField
              fullWidth
              label='Enter OTP'
              value={enteredOtp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setEnteredOtp(value);
                setOtpError('');
              }}
              placeholder='000000'
              inputProps={{
                maxLength: 6,
                style: {
                  fontSize: '24px',
                  textAlign: 'center',
                  letterSpacing: '8px',
                  fontWeight: 600,
                },
              }}
              sx={{
                '& input': {
                  textAlign: 'center',
                },
              }}
              disabled={otpSending}
              autoFocus
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant='caption' color='text.secondary'>
                OTP valid for 5 minutes
              </Typography>
              <Button
                size='small'
                onClick={handleOpenOtpDialog}
                disabled={otpSending}
                sx={{
                  color: getPrimaryColor(),
                  textTransform: 'none',
                  '&:hover': { bgcolor: alpha(getPrimaryColor(), 0.05) },
                }}
              >
                Resend OTP
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: alpha(getPrimaryColor(), 0.02) }}>
          <Button
            onClick={() => {
              setOtpDialogOpen(false);
              setOtpError('');
              setEnteredOtp('');
            }}
            disabled={otpSending}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleVerifyOtpAndExport}
            disabled={enteredOtp.length !== 6 || otpSending}
            startIcon={<CheckCircleIcon />}
            sx={{
              minWidth: 120,
              bgcolor: getPrimaryColor(),
              boxShadow: `0 2px 8px ${alpha(getPrimaryColor(), 0.3)}`,
              '&:hover': {
                bgcolor: getPrimaryLightColor(),
                boxShadow: `0 4px 12px ${alpha(getPrimaryColor(), 0.4)}`,
              },
            }}
          >
            Verify & Export
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserManagement;
